/**
 * receivingLedger — BQC4 reconciliation: which WIPs on an issued slip have
 * actually arrived back at the receiving station, and how much of each.
 *
 * A dispatch slip is a promise, not a receipt: `wipIds` says what went OUT.
 * What has come BACK is a separate fact, and this module is where that fact
 * lives — one append-only scan per WIP, the same soft-undo shape as
 * iSyn2_CutReadyScans, never a field rewritten on the slip itself.
 *
 * THAT IS A DELIBERATE BOUNDARY, NOT AN OMISSION. The legacy workbook tracked
 * an InTransit → Open → Reconciled state on the slip; this migration does not
 * port it, because the slip's own schema is closed (see
 * Isyn2ProvisioningService's "never add fields to a list it didn't just
 * create" rule) and because a slip Choice value can drift from the scans that
 * are supposed to justify it. Reconciliation here is DERIVED, every time,
 * from summing a WIP's active receiving scans against its own qty — so it
 * can never say "complete" while sets are still missing, or "incomplete"
 * once every set is actually back.
 *
 * BY THE SET, NOT JUST BY THE WIP. The receiving station takes a typed set
 * count per scan (a delivery can be short a few sets within a WIP, not just
 * short whole WIPs), so a WIP can legitimately be scanned more than once —
 * a first delivery of 1 of 2 sets, a later one for the remaining set. Every
 * figure here sums the active scans rather than checking "does at least one
 * exist", which is what keeps a partial delivery from reading as whole.
 *
 * ADVISE, NEVER BLOCK, same as the rest of this module's station screens: a
 * scan that does not match the paper trail (no issued slip, a cancelled one,
 * one that would push a WIP past its own qty) is still recorded, with a
 * warning attached — refusing it would turn the operator's paperwork
 * problem into a lost scan.
 *
 * ONE NARROW, DELIBERATE EXCEPTION to "scans only": a HISTORICAL slip (see
 * ISlip.historical) can never get a scan in the first place — the paper
 * process that generated it predates this app. historicalReceiveTally /
 * historicalSlipReceiveSummary / historicalSlipReceiveSetsSummary below
 * read the workbook's own imported facts instead, for those slips only —
 * an app-issued slip's reconciliation still never touches anything but its
 * own scan log.
 */
import type { ISlip } from "../../lib/sp/DispatchService";

export interface IReceivingScan {
  spId: number;
  wipId: string;
  /** The slip this was received against, recorded at scan time. */
  dsNumber: string;
  /** Sets covered by THIS scan — not the WIP's total. */
  qty: number;
  receivedBy: string;
  receivedAt: string;
  /** Set when the scan was undone (mis-scan). Soft — the row stays as history. */
  undoneBy?: string;
  undoneAt?: string;
  /** True when this scan is a set physically coming back from an external
   * repair (QC found it failed, the subcontractor reworks it, it re-enters
   * the building) rather than a first delivery against the slip. Auto-set
   * by the station from whether QC currently has any of this WIP's sets on
   * an outstanding repair hold at scan time — see Receiving.tsx. Purely
   * informational: it does not change how received/awaiting/complete are
   * computed, only how the scan is labelled. */
  repairReturn?: boolean;
  /** Last time this scan was folded into the RECEIVING outbox CSV that
   * AutoWriteReceiving.bas merges into the legacy RECEIVING USERFORM.xlsb
   * workbook's own RECEIVING sheet — see receivingOutbox.ts. Absent means
   * never exported. Stamped per SCAN, not per WIP: a RECEIVING row's own QTY
   * is a running total across a WIP's active scans, so a later partial
   * delivery must show up as "not yet synced" on its own even though
   * earlier scans for the same WIP already were. */
  syncedToWorkbookAt?: string;
}

/** The active (non-undone) scans only — an undone scan did not happen. */
function active(scans: readonly IReceivingScan[]): IReceivingScan[] {
  return scans.filter(s => !s.undoneAt);
}

/**
 * The slip that issued this WIP, or null.
 *
 * Only an Issued slip counts. A Cancelled slip already freed its WIPs, so a
 * receiving scan cannot be reconciling paperwork that no longer claims them —
 * canReceive() surfaces that case as a warning rather than pretending the
 * cancelled slip is still the right one to match against.
 */
export function findSlipForWip(wipId: string, slips: readonly ISlip[]): ISlip | null {
  const id = (wipId ?? "").trim();
  if (!id) return null;
  return slips.find(s => s.slipStatus === "Issued" && s.wipIds.includes(id)) ?? null;
}

/** WIPs with at least one active receiving scan, regardless of how much of
 * their qty it covers — "has this WIP been touched at all". */
export function receivedWipIds(scans: readonly IReceivingScan[]): Set<string> {
  return new Set(active(scans).map(s => s.wipId));
}

export interface IReceiveTally {
  wipId: string;
  /** The WIP's total sets — what everything below has to add up to. */
  qty: number;
  /** Cumulative sets received, active scans only. Capped at qty. */
  received: number;
  /** Sets never scanned back in at all. qty − received. */
  awaiting: number;
  /** Every set has come back. */
  complete: boolean;
}

/**
 * Sum a WIP's active receiving scans into where it currently stands.
 *
 * `received` is capped against `qty` — a duplicate or over-reported scan
 * must not read as "120% received", the same discipline qcLedger's tally
 * holds on the QC side of this same pipeline.
 */
export function receiveTally(wipId: string, qty: number, scans: readonly IReceivingScan[]): IReceiveTally {
  const id = (wipId ?? "").trim();
  const total = Math.max(0, qty || 0);
  let received = 0;
  for (const s of active(scans)) {
    if (s.wipId !== id) continue;
    received += Math.max(0, s.qty || 0);
  }
  received = Math.min(received, total);
  return { wipId: id, qty: total, received, awaiting: Math.max(0, total - received), complete: total > 0 && received >= total };
}

export interface ISlipReceiveSummary {
  /** WIPs fully received (every set back), not merely touched. */
  received: number;
  total: number;
  reconciled: boolean;
  /** WIPs with at least one set still outstanding — partially received counts. */
  incompleteWipIds: string[];
}

/**
 * How much of a slip has come back, by WIP.
 *
 * `reconciled` is true only when every one of the slip's own WIPs is fully
 * received — a WIP holding back even one set still counts as incomplete, so
 * this can never say "reconciled" while a subcontractor is still holding
 * part of a bundle. `qtyOf` is the caller's own WIP→qty lookup (Production
 * Grid data), kept out of this module's own state the same way slipEdit.ts's
 * editTotals takes one.
 */
export function slipReceiveSummary(
  slip: ISlip,
  scans: readonly IReceivingScan[],
  qtyOf: (wipId: string) => number
): ISlipReceiveSummary {
  const incompleteWipIds: string[] = [];
  let received = 0;
  for (const id of slip.wipIds) {
    if (receiveTally(id, qtyOf(id), scans).complete) received++;
    else incompleteWipIds.push(id);
  }
  const total = slip.wipIds.length;
  return { received, total, reconciled: total > 0 && incompleteWipIds.length === 0, incompleteWipIds };
}

export interface ISlipReceiveSets {
  dispatchedSets: number;
  receivedSets: number;
  outstandingSets: number;
}

/**
 * The same reconciliation as slipReceiveSummary, in sets rather than WIPs —
 * "9 of 10 WIPs" and "18 of 20 sets" are two different readings of one
 * partial delivery, and a subcontractor thinks in sets. Sums the ACTUAL
 * received qty per WIP (via receiveTally), not the WIP's full qty the
 * moment any scan exists — the latter would overstate a delivery that is
 * itself only partial within a WIP.
 */
export function slipReceiveSetsSummary(
  slip: ISlip,
  scans: readonly IReceivingScan[],
  qtyOf: (wipId: string) => number
): ISlipReceiveSets {
  let dispatchedSets = 0, receivedSets = 0;
  for (const id of slip.wipIds) {
    const t = receiveTally(id, qtyOf(id), scans);
    dispatchedSets += t.qty;
    receivedSets += t.received;
  }
  return { dispatchedSets, receivedSets, outstandingSets: dispatchedSets - receivedSets };
}

/**
 * The workbook-imported facts a HISTORICAL slip's reconciliation is built
 * from — a subset of IProductionWip, kept narrow so this module doesn't
 * need to import the whole grid type for three fields.
 */
export interface IWorkbookReceiveInfo {
  receivedQty?: number;
  receivedAt?: string;
  grn?: string;
}

/**
 * historicalReceiveTally / historicalSlipReceiveSummary /
 * historicalSlipReceiveSetsSummary — the ONE deliberate exception to this
 * module's own "reconciliation is derived from the scan log, never the
 * workbook" rule (see the file header), narrowly scoped to slips flagged
 * `historical` (imported from the legacy workbook's own BQC3 sheet, never
 * issued by this app — see historicalImport.ts).
 *
 * A historical slip structurally can never get an iSyn2_ReceivingScans
 * entry of its own: the receiving team's paper process that generated it
 * predates this app, and scans-only reconciliation would therefore read
 * every one of these slips as permanently outstanding even once the
 * subcontractor genuinely delivered — which is exactly the gap that kept
 * hundreds of already-received historical WIPs invisible to DS ↔ DR Recon
 * (2026-08-18 design conversation with Mikko). The workbook's own BQC4
 * sheet already carries the real facts (DATE RECEIVED / QTY / GRN, read
 * into IProductionWip.receivedQty/receivedAt/grn by
 * parseProductionGridWorkbook.ts) — these three functions are the WIP/slip
 * reconciliation shape (receiveTally/slipReceiveSummary/
 * slipReceiveSetsSummary), fed from that instead of scans.
 *
 * This does NOT apply to app-issued slips — those keep reconciling from
 * scans exactly as before, untouched. A live slip's status must never be
 * able to drift based on workbook data; only a slip that can never get a
 * scan in the first place is allowed to read its status from the workbook.
 */
export function historicalReceiveTally(wipId: string, qty: number, info: IWorkbookReceiveInfo | undefined): IReceiveTally {
  const id = (wipId ?? "").trim();
  const total = Math.max(0, qty || 0);
  const received = Math.min(Math.max(0, info?.receivedQty ?? 0), total);
  return { wipId: id, qty: total, received, awaiting: Math.max(0, total - received), complete: total > 0 && received >= total };
}

export function historicalSlipReceiveSummary(
  slip: ISlip,
  wipOf: (wipId: string) => IWorkbookReceiveInfo | undefined,
  qtyOf: (wipId: string) => number
): ISlipReceiveSummary {
  const incompleteWipIds: string[] = [];
  let received = 0;
  for (const id of slip.wipIds) {
    if (historicalReceiveTally(id, qtyOf(id), wipOf(id)).complete) received++;
    else incompleteWipIds.push(id);
  }
  const total = slip.wipIds.length;
  return { received, total, reconciled: total > 0 && incompleteWipIds.length === 0, incompleteWipIds };
}

export function historicalSlipReceiveSetsSummary(
  slip: ISlip,
  wipOf: (wipId: string) => IWorkbookReceiveInfo | undefined,
  qtyOf: (wipId: string) => number
): ISlipReceiveSets {
  let dispatchedSets = 0, receivedSets = 0;
  for (const id of slip.wipIds) {
    const t = historicalReceiveTally(id, qtyOf(id), wipOf(id));
    dispatchedSets += t.qty;
    receivedSets += t.received;
  }
  return { dispatchedSets, receivedSets, outstandingSets: dispatchedSets - receivedSets };
}

export type ReceiveVerdict = "ok" | "not-on-any-slip" | "slip-cancelled" | "already-complete" | "exceeds-remaining";

export interface IReceiveCheck {
  /** Always true — receiving never refuses a scan, see the file header. */
  ok: true;
  verdict: ReceiveVerdict;
  /** Shown as a warning banner; empty when verdict is "ok". */
  reason: string;
}

/**
 * Should this scan raise a warning, and what should it say?
 *
 * The result is always `ok: true` — there is no verdict here that stops the
 * write. A second (or third) scan of the same WIP is expected, not a
 * mistake — it is how a delivery split across days gets recorded — so the
 * "already" warning fires only once every set is already back, and a typed
 * count bigger than what is actually outstanding is flagged rather than
 * silently accepted.
 */
export function canReceive(
  wipId: string,
  qty: number,
  submitQty: number,
  slips: readonly ISlip[],
  scans: readonly IReceivingScan[]
): IReceiveCheck {
  const warn = (verdict: ReceiveVerdict, reason: string): IReceiveCheck => ({ ok: true, verdict, reason });
  const id = (wipId ?? "").trim();
  if (!id) return warn("not-on-any-slip", "No WIP scanned.");

  const issued = findSlipForWip(id, slips);
  if (!issued) {
    const cancelled = slips.find(s => s.slipStatus === "Cancelled" && s.wipIds.includes(id));
    if (cancelled) {
      return warn(
        "slip-cancelled",
        `${id} was on ${cancelled.dsNumber}, which is cancelled — recording the receipt anyway, but check it isn't a duplicate.`
      );
    }
    return warn(
      "not-on-any-slip",
      `${id} is not on any issued slip — recording the receipt anyway, but check the WIP ID.`
    );
  }
  const tally = receiveTally(id, qty, scans);
  if (tally.complete) {
    return warn("already-complete", `${id} already has all ${tally.qty} set${tally.qty === 1 ? "" : "s"} received — this will record more.`);
  }
  if (submitQty > tally.awaiting) {
    return warn(
      "exceeds-remaining",
      `Only ${tally.awaiting} of ${id}'s sets are still outstanding — recording ${submitQty} anyway, but check the count.`
    );
  }
  return { ok: true, verdict: "ok", reason: "" };
}
